<template>
  <div class="sensitive py-1 space-y-1">
    <div
      v-for="(value, key) in extraInfo"
      :key="key"
      class="flex items-start gap-1.5"
    >
      <span class="text-xs text-gray-500 mt-0.5">•</span>
      <div class="flex-1">
        <span class="text-xs text-gray-600 font-medium">{{ formatKey(key) }}:</span>
        <span
          class="text-xs text-gray-900 ml-1"
          :class="getValueClass(key, value)"
        >
          {{ formatValue(key, value) }}
        </span>
      </div>
    </div>

    <!-- Show empty state if no data -->
    <div v-if="isEmpty" class="text-xs text-gray-400 italic">
      No additional information
    </div>
  </div>
</template>

<script>
import { calendarDate } from '@/utils/common'
export default {
  name: 'ExtraInfoCompositeRenderer',
  props: ['params'],
  computed: {
    extraInfo() {
      return this.params.data?.extra_info || {};
    },
    isEmpty() {
      return Object.keys(this.extraInfo).length === 0 ||
             !Object.values(this.extraInfo).some(v => v !== null && v !== undefined && v !== '');
    }
  },
  methods: {
    formatKey(key) {
      // Convert snake_case to Title Case
      return key
        .split('_')
        .map(word => {
          // Handle common abbreviations
          const upperCaseWords = ['dbts', 'ace', 'statn', 'id', 'pcp'];
          if (upperCaseWords.includes(word.toLowerCase())) {
            return word.toUpperCase();
          }
          return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
        })
        .join(' ');
    },

    formatValue(key, value) {
      if (value === null || value === undefined) return '-';

      // Check if it's a date field
      if (this.isDateField(key, value)) {
        return this.formatDate(value);
      }

      // Check if it's a number that should be formatted as currency
      if (this.isCurrencyField(key, value)) {
        return this.formatCurrency(value);
      }

      // Check if it's a days supply field
      if (this.isDaysSupplyField(key)) {
        return `${value} days`;
      }

      // Boolean values
      if (typeof value === 'boolean') {
        return value ? 'Yes' : 'No';
      }

      // Default: return as string
      return String(value);
    },

    getValueClass(key, value) {
      // Add color coding for days supply fields
      if (this.isDaysSupplyField(key)) {
        const days = parseInt(value);
        if (days <= 7) return 'font-semibold text-red-600';
        if (days <= 14) return 'font-semibold text-orange-600';
        if (days <= 30) return 'font-semibold text-yellow-600';
        return 'font-semibold text-green-600';
      }

      // Add bold for important fields
      if (this.isImportantField(key)) {
        return 'font-semibold';
      }

      return '';
    },

    isDateField(key, value) {
      // Check if key contains date-related words
      if (key.includes('date') || key.includes('_dt') || key.includes('thru') || key.includes('from') || key.includes('to')) {
        return true;
      }

      // Check if value looks like a date
      if (typeof value === 'string' && value.match(/^\d{4}-\d{2}-\d{2}/)) {
        return true;
      }

      return false;
    },

    isCurrencyField(key, value) {
      // Check if key contains currency-related words
      if (key.includes('amount') || key.includes('cost') || key.includes('price') || key.includes('fee')) {
        return true;
      }

      // Check if value looks like currency
      if (typeof value === 'string' && value.match(/^\$[\d,]+\.?\d*$/)) {
        return true;
      }

      return false;
    },

    isDaysSupplyField(key) {
      return key.includes('days_supply') || key.includes('supply_days');
    },

    isImportantField(key) {
      return key.includes('valid_thru') || key.includes('amount') || key.includes('status');
    },

    formatDate(dateStr) {
      if (!dateStr) return '';
      try {
        const date = calendarDate(dateStr);
        if (!isNaN(date.getTime())) {
          return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
          });
        }
      } catch {
        // If parsing fails, return original
      }
      return dateStr;
    },

    formatCurrency(value) {
      if (typeof value === 'number') {
        return `$${value.toFixed(2)}`;
      }
      if (typeof value === 'string' && !value.startsWith('$')) {
        const num = parseFloat(value);
        if (!isNaN(num)) {
          return `$${num.toFixed(2)}`;
        }
      }
      return value;
    }
  }
}
</script>
